// Room QR tokens + guest session JWTs (ADR-0007 §2). Pure WebCrypto
// (globalThis.crypto) — runs identically on Cloudflare Workers and Node 20+,
// no dependencies.
//
//  * Room token: stable HMAC-SHA256 over `v1|room:<n>` with CHAT_QR_SECRET —
//    printed inside each room's QR (`?room=<n>&t=<token>`). Verifying it is
//    the GC1 room binding; rotation = new secret + reprint.
//  * Guest JWT: short-lived HS256 JWT signed with the Supabase project JWT
//    secret (role=authenticated + conversation_id/room/lang claims) so the
//    guest client reads + subscribes DIRECTLY against PostgREST/Realtime
//    under RLS. Also verified by /api/chat/message at the edge.

import type { ChatLang } from './domain.js';
import { isChatLang } from './domain.js';
import { toBase64Url, fromBase64Url } from '@lemontaigu/platform';

const encoder = new TextEncoder();

async function hmacKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign', 'verify'],
  );
}

async function hmacSign(secret: string, payload: string): Promise<string> {
  const key = await hmacKey(secret);
  const sig = await crypto.subtle.sign('HMAC', key, encoder.encode(payload));
  return toBase64Url(new Uint8Array(sig));
}

/** Constant-time-ish comparison via WebCrypto verify (avoids string ===). */
async function hmacVerify(secret: string, payload: string, signature: string): Promise<boolean> {
  const key = await hmacKey(secret);
  let sigBytes: Uint8Array;
  try {
    sigBytes = fromBase64Url(signature);
  } catch {
    return false;
  }
  return crypto.subtle.verify('HMAC', key, sigBytes as BufferSource, encoder.encode(payload));
}

// ── Room QR tokens (GC1) ─────────────────────────────────────────────────────

const ROOM_TOKEN_VERSION = 'v1';

/** The stable token embedded in a room's printed QR. */
export function signRoomToken(secret: string, room: string): Promise<string> {
  return hmacSign(secret, `${ROOM_TOKEN_VERSION}|room:${room}`);
}

export function verifyRoomToken(secret: string, room: string, token: string): Promise<boolean> {
  return hmacVerify(secret, `${ROOM_TOKEN_VERSION}|room:${room}`, token);
}

// ── Guest session JWTs ───────────────────────────────────────────────────────

export interface GuestClaims {
  conversationId: string;
  room: string;
  lang: ChatLang;
}

/** Default guest session lifetime — rescan the room QR to renew. */
export const GUEST_JWT_TTL_SECONDS = 24 * 60 * 60;

export async function mintGuestJwt(
  jwtSecret: string,
  claims: GuestClaims,
  ttlSeconds: number = GUEST_JWT_TTL_SECONDS,
  nowSeconds: number = Math.floor(Date.now() / 1000),
): Promise<string> {
  const header = { alg: 'HS256', typ: 'JWT' };
  // role/aud = authenticated → PostgREST maps the request to the
  // `authenticated` DB role; RLS then scopes by the conversation_id claim.
  const payload = {
    role: 'authenticated',
    aud: 'authenticated',
    iss: 'lemontaigu-chat',
    conversation_id: claims.conversationId,
    room: claims.room,
    lang: claims.lang,
    iat: nowSeconds,
    exp: nowSeconds + ttlSeconds,
  };
  const head = toBase64Url(encoder.encode(JSON.stringify(header)));
  const body = toBase64Url(encoder.encode(JSON.stringify(payload)));
  const signature = await hmacSign(jwtSecret, `${head}.${body}`);
  return `${head}.${body}.${signature}`;
}

interface Jwk {
  kid?: string;
  kty: string;
  crv?: string;
  x?: string;
  y?: string;
}

/**
 * Verify a Supabase-issued staff ACCESS TOKEN and return its subject (the
 * auth user id), or null. Membership in public.staff is a separate check
 * (ChatStore.isStaffUser) — this only authenticates the token.
 *
 * Supabase Auth signs session tokens with its own per-project asymmetric key
 * (ES256, ECC P-256 — confirmed 2026-07-04, not the shared HS256 project JWT
 * secret used elsewhere in this file for OUR OWN guest JWTs, which we both
 * mint and verify ourselves and are unaffected by Supabase's signing choice).
 * Verifying an externally-issued token means fetching Supabase's public JWKS
 * and checking the signature against the matching key — no shared secret
 * involved. Not cached: this is the low-volume staff-reply path, not the
 * guest hot path, and skipping a cache avoids ever verifying against a
 * stale key set during Supabase's own key rotation.
 */
export async function verifySupabaseAccessToken(
  supabaseUrl: string,
  jwt: string,
  nowSeconds: number = Math.floor(Date.now() / 1000),
  fetchImpl: typeof fetch = fetch.bind(globalThis),
): Promise<{ sub: string } | null> {
  const parts = jwt.split('.');
  if (parts.length !== 3) return null;
  const [head, body, signature] = parts as [string, string, string];

  let header: { alg?: string; kid?: string };
  try {
    header = JSON.parse(new TextDecoder().decode(fromBase64Url(head))) as {
      alg?: string;
      kid?: string;
    };
  } catch {
    return null;
  }
  if (header.alg !== 'ES256') return null;

  let keys: Jwk[];
  try {
    const res = await fetchImpl(`${supabaseUrl.replace(/\/$/, '')}/auth/v1/.well-known/jwks.json`);
    if (!res.ok) return null;
    const jwks = (await res.json()) as { keys?: Jwk[] };
    keys = jwks.keys ?? [];
  } catch {
    return null;
  }
  const candidates = header.kid ? keys.filter((k) => k.kid === header.kid) : keys;

  let verified = false;
  for (const jwk of candidates) {
    try {
      const key = await crypto.subtle.importKey(
        'jwk',
        { kty: jwk.kty, crv: jwk.crv, x: jwk.x, y: jwk.y },
        { name: 'ECDSA', namedCurve: 'P-256' },
        false,
        ['verify'],
      );
      const ok = await crypto.subtle.verify(
        { name: 'ECDSA', hash: 'SHA-256' },
        key,
        fromBase64Url(signature) as BufferSource,
        encoder.encode(`${head}.${body}`),
      );
      if (ok) {
        verified = true;
        break;
      }
    } catch {
      // Try the next candidate key.
    }
  }
  if (!verified) return null;

  let payload: Record<string, unknown>;
  try {
    payload = JSON.parse(new TextDecoder().decode(fromBase64Url(body))) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (typeof payload.exp !== 'number' || payload.exp <= nowSeconds) return null;
  if (payload.role !== 'authenticated') return null;
  // Guest session JWTs are HS256 (rejected above by the alg check already),
  // but keep this belt-and-suspenders so a guest token can never reach the
  // staff path even if that ever changes.
  if (typeof payload.conversation_id === 'string') return null;
  if (typeof payload.sub !== 'string' || payload.sub === '') return null;
  return { sub: payload.sub };
}

/** Verified claims, or null (bad signature, malformed, expired, wrong shape). */
export async function verifyGuestJwt(
  jwtSecret: string,
  jwt: string,
  nowSeconds: number = Math.floor(Date.now() / 1000),
): Promise<GuestClaims | null> {
  const parts = jwt.split('.');
  if (parts.length !== 3) return null;
  const [head, body, signature] = parts as [string, string, string];

  if (!(await hmacVerify(jwtSecret, `${head}.${body}`, signature))) return null;

  let payload: Record<string, unknown>;
  try {
    payload = JSON.parse(new TextDecoder().decode(fromBase64Url(body))) as Record<string, unknown>;
  } catch {
    return null;
  }

  if (typeof payload.exp !== 'number' || payload.exp <= nowSeconds) return null;
  const conversationId = payload.conversation_id;
  const room = payload.room;
  const lang = payload.lang;
  if (typeof conversationId !== 'string' || conversationId === '') return null;
  if (typeof room !== 'string' || room === '') return null;
  if (typeof lang !== 'string' || !isChatLang(lang)) return null;

  return { conversationId, room, lang };
}
